import {
	type KeyboardEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	useCurrentRefinements,
	useInstantSearchContext,
} from 'react-instantsearch';
import * as autocompleteCore from '@algolia/autocomplete-core';
import { type AutocompleteSource } from '@algolia/autocomplete-core';
import * as autocompletePresetAlgolia from '@algolia/autocomplete-preset-algolia';
import { type SearchClient } from '@algolia/autocomplete-preset-algolia';
import client from '@searchkit/instantsearch-client';

import { type Repub } from '@cx-modules/constants';

import { type Facet } from '~/components/BettingInsights/components/Search/AutocompleteDropdown';

const { createAutocomplete } = autocompleteCore;
const { getAlgoliaFacets } = autocompletePresetAlgolia;

function createSearchClient({ repub }: { repub: Repub }) {
	const searchClient = client({
		headers: {
			'OC-Repub': repub,
		},
		url: '/api/cx/v2/search',
	});

	// @ts-ignore
	searchClient.transporter = {
		headers: {
			'x-algolia-application-id': 'NULL',
			'x-algolia-api-key': 'NULL',
		},
	};

	return searchClient as unknown as SearchClient;
}

let timer: NodeJS.Timeout | undefined = undefined;

function debounceSources(
	args: AutocompleteSource<Facet>[],
	time: number,
): Promise<AutocompleteSource<Facet>[]> {
	if (timer) {
		clearTimeout(timer);
	}

	return new Promise(resolve => {
		timer = setTimeout(() => resolve(args), time);
	});
}

export const useSearchWithAutocomplete = ({
	indexName,
	inputRef,
	repub,
	searchAttributes,
	setInputValue,
	setIsDropdownVisible,
}: {
	indexName: string;
	inputRef: React.RefObject<HTMLInputElement>;
	repub: Repub;
	searchAttributes: string[];
	setInputValue: (value: string) => void;
	setIsDropdownVisible: (value: boolean) => void;
}) => {
	const { items: currentRefinements } = useCurrentRefinements({
		includedAttributes: ['eventDisplayName'],
	});
	const { helper } = useInstantSearchContext();
	const searchClient = useMemo(() => createSearchClient({ repub }), [repub]);
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [searchResults, setSearchResults] = useState<string[]>([]);
	const [activeIndex, setActiveIndex] = useState(-1);

	const handleClearSearchResults = useCallback(() => {
		setSearchResults([]);
	}, []);

	useEffect(() => {
		if (!helper) return;

		const filters = selectedTags
			.map(tag =>
				searchAttributes.map(facet => `${facet}:"${tag}"`).join(' OR '),
			)
			.reduce((acc, tagQuery, index) => {
				if (index > 0) {
					return `${acc} AND (${tagQuery})`;
				}
				return `(${tagQuery})`;
			}, '');

		// @ts-expect-error crap algolia typings
		helper.setQueryParameter('filters', filters);
		// @ts-expect-error crap algolia typings
		helper.setQueryParameter('page', 0);
		helper.search();
	}, [helper, selectedTags, searchAttributes]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>, onEnter: () => void) => {
			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setActiveIndex(prevIndex =>
						prevIndex < searchResults.length - 1 ? prevIndex + 1 : prevIndex,
					);
					break;
				case 'ArrowUp':
					e.preventDefault();
					setActiveIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : -1));
					break;
				case 'Enter':
					e.preventDefault();
					onEnter();
					break;
				default:
					return;
			}
		},
		[searchResults],
	);

	const autocomplete = useMemo(
		() =>
			createAutocomplete<
				Facet,
				React.FormEvent,
				React.MouseEvent,
				React.KeyboardEvent
			>({
				onReset() {
					setActiveIndex(-1);
					setSearchResults([]);
					setSelectedTags([]);
					setIsDropdownVisible(false);
					setInputValue('');
					autocomplete.setQuery('');
				},
				onStateChange({ state }) {
					const results = state.collections.flatMap(collection =>
						collection.items.map(item => item.label),
					);
					setInputValue(state.query);
					setSearchResults(results);
					setIsDropdownVisible(results.length > 0);
				},
				onSubmit({ state }) {
					setSelectedTags(tags => [...tags, state.query]);
					setActiveIndex(-1);
					setSearchResults([]);
					setIsDropdownVisible(false);
					setInputValue('');
				},
				getSources() {
					return debounceSources(
						[
							{
								sourceId: 'searchAttributes',
								getItemInputValue({ item }) {
									return item.label;
								},
								getItems({ query }) {
									return getAlgoliaFacets<Facet>({
										searchClient,
										queries: searchAttributes.map(facet => ({
											indexName,
											facet,
											params: {
												facetName: facet,
												facetQuery: query,
												maxFacetHits: 1,
												...(currentRefinements.length > 0 &&
												currentRefinements[0].refinements.length > 0
													? {
															filters: `eventDisplayName:"${currentRefinements[0].refinements[0].value}"`,
														}
													: {}),
											},
											type: 'facet' as const,
										})),
										transformResponse(response) {
											const validResults = response.results.filter(
												result => result && 'facetHits' in result,
											);

											const seenValues = new Set<string>();
											return validResults.map(result =>
												result.facetHits
													.filter(hit => {
														if (seenValues.has(hit.value)) {
															return false;
														}
														seenValues.add(hit.value);
														return true;
													})
													.map(hit => ({
														count: hit.count,
														label: hit.value,
														value: hit.value,
														_highlightResult: {
															label: {
																value: hit.highlighted || hit.value,
																matchLevel: 'full',
																matchedWords: [],
															},
														},
													})),
											);
										},
									});
								},
							},
						],
						300,
					);
				},
			}),
		[
			indexName,
			searchAttributes,
			searchClient,
			setInputValue,
			setIsDropdownVisible,
			currentRefinements,
		],
	);

	return {
		activeIndex,
		autocomplete,
		getInputProps: () =>
			autocomplete.getInputProps({ inputElement: inputRef.current }),
		handleClearSearchResults,
		handleKeyDown,
		searchResults,
		selectedTags,
		setActiveIndex,
		setSearchResults,
		setSelectedTags,
	};
};
